import { lazy, Suspense, useEffect } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Shell } from '@/components/layout/Shell';
import { SettingsLayout } from '@/pages/settings/SettingsLayout';
import { RequireAuth } from '@/components/RequireAuth';
import { RequireRole } from '@/components/RequireRole';
import { RequirePlatform } from '@/components/RequirePlatform';
import { LogoLoading } from '@/components/ui/LogoLoading';
import { Dashboard } from '@/pages/Dashboard';
import { Login } from '@/pages/Login';
import { Signup } from '@/pages/Signup';
import { SignupEmail } from '@/pages/SignupEmail';
import { Onboarding } from '@/pages/Onboarding';
import { NoCompany } from '@/pages/NoCompany';
import { NotFound } from '@/pages/NotFound';

// Wave-2 modules: Customers, Vendors, Chart of Accounts, Bank Accounts, Journal.
/* The whole staff console in one chunk: eight pages, one destination, and
   nobody reaches /admin/stripe without reaching /admin. */
const AdminConsole = lazyChunk(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminConsole })),
);
const InvitePage = lazyChunk(() => import('@/pages/Invite').then((m) => ({ default: m.Invite })));
const StartPage = lazyChunk(() => import('@/pages/start/Start').then((m) => ({ default: m.Start })));
const IntakePage = lazyChunk(() =>
  import('@/pages/start/Intake').then((m) => ({ default: m.Intake })),
);
const CustomersRoutes = lazyChunk(() =>
  import('@/pages/customers').then((m) => ({ default: m.CustomersRoutes })),
);
const VendorsRoutes = lazyChunk(() =>
  import('@/pages/vendors').then((m) => ({ default: m.VendorsRoutes })),
);
const ChartOfAccounts = lazyChunk(() =>
  import('@/pages/accounting/ChartOfAccounts').then((m) => ({ default: m.ChartOfAccounts })),
);
const BankAccounts = lazyChunk(() =>
  import('@/pages/accounting/BankAccounts').then((m) => ({ default: m.BankAccounts })),
);
const JournalViewer = lazyChunk(() =>
  import('@/pages/accounting/JournalViewer').then((m) => ({ default: m.JournalViewer })),
);


// Wave-3 Group A: Invoices, Quotations, Bills, Expenses.
const Invoices = lazyChunk(() =>
  import('@/pages/invoices').then((m) => ({ default: m.InvoicesRoutes })),
);
const CreditNotes = lazyChunk(() =>
  import('@/pages/credit-notes').then((m) => ({ default: m.CreditNotesRoutes })),
);
const Quotations = lazyChunk(() =>
  import('@/pages/quotations').then((m) => ({ default: m.QuotationsRoutes })),
);
const Bills = lazyChunk(() =>
  import('@/pages/bills').then((m) => ({ default: m.BillsRoutes })),
);
const Expenses = lazyChunk(() =>
  import('@/pages/expenses').then((m) => ({ default: m.ExpensesRoutes })),
);
const Payments = lazyChunk(() =>
  import('@/pages/payments').then((m) => ({ default: m.PaymentsRoutes })),
);
/* The catalogue is NOT gated on the inventory module and the stock screens
   are — see the Sidebar. The route itself stays open either way: the server is
   the gate, and a 403 inside the page reads better than a 404 on the URL
   somebody was sent. */
const Items = lazyChunk(() => import('@/pages/items').then((m) => ({ default: m.ItemsRoutes })));
const Inventory = lazyChunk(() =>
  import('@/pages/inventory').then((m) => ({ default: m.InventoryRoutes })),
);
const Employees = lazyChunk(() =>
  import('@/pages/employees').then((m) => ({ default: m.EmployeesRoutes })),
);
const Payroll = lazyChunk(() =>
  import('@/pages/payroll').then((m) => ({ default: m.PayrollRoutes })),
);
const Leave = lazyChunk(() => import('@/pages/leave').then((m) => ({ default: m.LeaveRoutes })));
const MyLeavePage = lazyChunk(() =>
  import('@/pages/MyLeave').then((m) => ({ default: m.MyLeave })),
);
const Subscriptions = lazyChunk(() =>
  import('@/pages/subscriptions').then((m) => ({ default: m.SubscriptionsRoutes })),
);
const Contracts = lazyChunk(() =>
  import('@/pages/contracts').then((m) => ({ default: m.ContractsRoutes })),
);
const Reports = lazyChunk(() =>
  import('@/pages/reports').then((m) => ({ default: m.ReportsRoutes })),
);
const Settings = lazyChunk(() =>
  import('@/pages/settings').then((m) => ({ default: m.SettingsRoutes })),
);
const Users = lazyChunk(() =>
  import('@/pages/users').then((m) => ({ default: m.UsersRoutes })),
);
const Billing = lazyChunk(() =>
  import('@/pages/settings/Billing').then((m) => ({ default: m.Billing })),
);
const Account = lazyChunk(() => import('@/pages/Account').then((m) => ({ default: m.Account })));
const ResetPasswordPage = lazyChunk(() =>
  import('@/pages/ResetPassword').then((m) => ({ default: m.ResetPassword })),
);
const DraftsPage = lazyChunk(() => import('@/pages/drafts/List').then((m) => ({ default: m.DraftsList })));
const ActivityPage = lazyChunk(() => import('@/pages/activity/List').then((m) => ({ default: m.ActivityList })));
const FilesPage = lazyChunk(() => import('@/pages/files/List').then((m) => ({ default: m.FilesList })));
const MemoryPage = lazyChunk(() => import('@/pages/Memory').then((m) => ({ default: m.MemoryPage })));

function Loading(): ReactNode {
  return (
    <div className="flex min-h-60 items-center justify-center">
      <LogoLoading size={72} />
    </div>
  );
}

/**
 * A LAZY IMPORT THAT SURVIVES A DEPLOY.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BLACK SCREEN
 *
 * Every route below is a separate hashed chunk. When a release ships, the
 * hashes change and the old files stop existing. A browser that has had this
 * app open across a deploy is still running the OLD index.html, so the moment
 * somebody navigates to a page they have not visited yet it asks for a chunk
 * that is no longer there, the import rejects, Suspense has nothing to render,
 * and the screen goes black. Pressing refresh fetches the new index.html and
 * everything works, which is exactly why this reads as "the page is broken
 * until I reload" rather than as a deploy.
 *
 * It is invisible in development, where nothing is hashed, and invisible in
 * testing, where nobody has the app open during a release. It was reported by
 * the customer on a day with nine deploys.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO DEFENCES, AND THIS IS THE SECOND
 *
 * The first is in infra/aws/deploy.sh: a release now inherits the previous
 * release's assets, so an old hash keeps resolving. That fixes the common case
 * without the customer noticing anything.
 *
 * This is what happens when that is not enough: three deploys later the release
 * holding their chunk has been pruned, or a proxy served a stale index. Rather
 * than a black screen, the page reloads itself once.
 *
 * ONCE is the whole design. A reload loop on a genuinely missing file is worse
 * than a blank page: it is a blank page that also burns battery and cannot be
 * read. The flag lives in sessionStorage so a second failure in the same tab
 * falls through to the error, and a new tab gets one fresh attempt.
 */
const RELOAD_FLAG = 'railit_chunk_reloaded';

function lazyChunk<T extends ComponentType<unknown>>(load: () => Promise<{ default: T }>) {
  return lazy(() =>
    load().catch((err: unknown) => {
      let already = 'yes';
      try {
        already = sessionStorage.getItem(RELOAD_FLAG) ?? '';
        sessionStorage.setItem(RELOAD_FLAG, 'yes');
      } catch {
        /* Private mode, or storage disabled. Fall through to the error rather
           than reloading blind, which is the case that loops. */
      }
      if (!already) {
        window.location.reload();
        /* Never resolves: the reload is already in flight and rendering an
           error for a page that is about to be replaced is a flash of noise. */
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );
}

/* Cleared on a successful load, so the next deploy gets its own attempt rather
   than inheriting a flag set weeks ago. */
function clearReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* ignore */
  }
}

function Lazy({ Component }: { Component: ComponentType }): ReactNode {
  useEffect(clearReloadFlag, []);
  return (
    <Suspense fallback={<Loading />}>
      <Component />
    </Suspense>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/signup/email" element={<SignupEmail />} />
      {/* Outside RequireAuth on purpose: an account here holds an onboarding
          token, not a session, and has no organization to be authorised for. */}
      <Route path="/onboarding" element={<Onboarding />} />
      {/* Same reasoning as /onboarding: the person here holds an onboarding
          token and no session, because they are in no company to be authorised
          for. It is the door to the wizard, not a second wizard. */}
      <Route path="/no-company" element={<NoCompany />} />
      {/* Public for the same reason: the token IS the credential, and the
          person holding it has no account yet in the common case. */}
      <Route path="/invite/:token" element={<Lazy Component={InvitePage} />} />
      {/*
        THE PAGE EVERY PASSWORD RESET EMAIL POINTS AT, and it was not here.

        The service minted the token, the email sent the link, and the API
        accepted it. The only missing piece was the screen, so every reset this
        product has ever sent landed on "the route you tried to open does not
        exist". Public for the same reason as the invite above: the token IS the
        credential, and the person holding it cannot sign in by definition.
      */}
      <Route path="/reset" element={<Lazy Component={ResetPasswordPage} />} />

      <Route element={<RequireAuth />}>
        {/*
          BRINGING A COMPANY IN, OUTSIDE THE SHELL.

          Authenticated, there is a workspace by now, and the intake writes
          into it, but deliberately not inside the sidebar chrome. This is the
          first screen a new workspace sees and it is asking one question; a
          navigation rail listing forty pages full of nothing is the exact
          answer it is trying to avoid giving.

          The id is in the URL so a refresh reconnects to the same import rather
          than starting a second one. Losing the connection must not lose the
          work: see backend/src/lib/intakeEvents.ts.
        */}
        <Route path="/start" element={<Lazy Component={StartPage} />} />
        <Route path="/start/files" element={<Lazy Component={IntakePage} />} />
        <Route path="/start/files/:id" element={<Lazy Component={IntakePage} />} />

        {/*
          THE STAFF CONSOLE, OUTSIDE THE TENANT SHELL AND BEHIND ITS OWN GUARD.

          Outside `<Shell>` because everything the shell draws belongs to ONE
          company: the customer sidebar, the workspace switcher, that
          workspace's AI credit bar, the trial band, and the dialog that appears
          when that workspace cannot write. None of it applies to a page about
          every other company, and the credit bar in particular reported a
          figure belonging to whichever tenant the staff member's token happened
          to name. The console brings its own frame; see pages/admin/AdminLayout.

          RequirePlatform WRAPS the element and must never move inside it.
          React Router renders a layout route's element BEFORE the matched
          child's, so a guard on the children would paint the entire staff
          navigation for any signed-in customer who typed /admin, with only the
          innermost panel refusing. The two orderings look identical in review.

          `/admin` itself stays a working address: it is in
          backend/scripts/check-pages-load.ts, in the nginx redirect in
          infra/aws/deploy.sh, and in every platform alert email.
        */}
        <Route
          path="/admin/*"
          element={
            <RequirePlatform>
              <Lazy Component={AdminConsole} />
            </RequirePlatform>
          }
        />

        <Route element={<Shell />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />

          <Route path="/customers/*" element={<Lazy Component={CustomersRoutes} />} />
          <Route path="/vendors/*" element={<Lazy Component={VendorsRoutes} />} />

          <Route
            path="/accounting/chart-of-accounts"
            element={<Lazy Component={ChartOfAccounts} />}
          />
          {/*
            BALANCES ARE FINANCE, NOT GENERAL READING.

            Owner is on the list because leaving them off would lock the account
            holder out of their own bank page, which no permissions model means
            by "Admin and Finance". Viewer is deliberately off: a Viewer can read
            expenses, and could therefore reach a screen showing every balance
            and a truncated IBAN.

            The API narrows to match rather than merely refusing. GET
            /bank-accounts is also the picker an expense form uses, and expenses
            can be filed by Sales, Engineer and Designer, so the route stays open
            to them and drops `balance` and `iban` from the answer instead. See
            backend/src/routes/erp/bankAccounts.ts.
          */}
          <Route
            path="/accounting/bank-accounts"
            element={
              <RequireRole allow={['Owner', 'Admin', 'Finance']}>
                <Lazy Component={BankAccounts} />
              </RequireRole>
            }
          />
          <Route path="/accounting/journal" element={<Lazy Component={JournalViewer} />} />
          {/*
            Everything staged and waiting, from every surface at once.

            Under /accounting because that is where the rail files it, and NOT
            under the assistant: a draft is also staged by the payment
            correction dialog in a workspace with the `ai` module switched off,
            so filing this under the chat panel would hide it from the tenant
            most likely to have lost one. The API mount is ungated for the same
            reason (routes/erp/drafts.ts).

            Owner, Admin and Finance only, like /activity below and for the same
            sentence: a preview of an employee update shows a salary.
          */}
          <Route
            path="/drafts"
            element={
              <RequireRole allow={['Owner', 'Admin', 'Finance']}>
                <Lazy Component={DraftsPage} />
              </RequireRole>
            }
          />

          <Route path="/invoices/*" element={<Lazy Component={Invoices} />} />
          <Route path="/credit-notes/*" element={<Lazy Component={CreditNotes} />} />
          <Route path="/quotations/*" element={<Lazy Component={Quotations} />} />
          <Route path="/bills/*" element={<Lazy Component={Bills} />} />
          <Route path="/expenses/*" element={<Lazy Component={Expenses} />} />
          <Route path="/payments/*" element={<Lazy Component={Payments} />} />
          <Route path="/items/*" element={<Lazy Component={Items} />} />
          <Route path="/inventory/*" element={<Lazy Component={Inventory} />} />
          <Route path="/employees/*" element={<Lazy Component={Employees} />} />
          <Route path="/payroll/*" element={<Lazy Component={Payroll} />} />
          <Route path="/leave/*" element={<Lazy Component={Leave} />} />
          {/*
            Your own leave, and deliberately NOT under RequireRole. A Viewer is
            still a person who gets holidays, and every request this page makes
            resolves the employee from the session, there is no id to tamper
            with, so there is nothing a role could usefully gate. Same reasoning
            as /account above.
          */}
          <Route path="/my-leave" element={<Lazy Component={MyLeavePage} />} />
          <Route path="/subscriptions/*" element={<Lazy Component={Subscriptions} />} />
          <Route path="/contracts/*" element={<Lazy Component={Contracts} />} />
          <Route path="/reports/*" element={<Lazy Component={Reports} />} />
          {/*
            Billing is its OWN page, deliberately outside the settings rail.
            It is not configuration — it is the subscription, three priced
            tiers, the AI meter and the add-on catalogue, and none of that can
            be compared inside the ~640px column the rail leaves behind. It
            keeps the app shell (sidebar, topbar); it just does not inherit
            Settings' second navigation.
          */}
          <Route
            path="/billing"
            element={
              <RequireRole allow={['Owner', 'Admin']}>
                <Lazy Component={Billing} />
              </RequireRole>
            }
          />
          {/* Everything already links to the old path. Keep it working. */}
          <Route path="/settings/billing" element={<Navigate to="/billing" replace />} />

          {/*
            Your account, not your company. Deliberately NOT under RequireRole:
            a Viewer in one company is still the owner of their own identity,
            and this is the only page that reaches every company they belong to.
          */}
          <Route path="/account" element={<Lazy Component={Account} />} />
          {/* Files. No module key: every workspace has documents, and the page
              holding a company's trade licence must not disappear when somebody
              switches Inventory off. */}
          <Route path="/files" element={<Lazy Component={FilesPage} />} />

          {/*
            Settings, People, the activity log and what the agent remembers all
            share ONE shell, and the rail is a layout rather than a page, so
            moving between them does not throw you out of configuration.

            THE URLS DO NOT MOVE. /activity and /memory keep the addresses they
            have always had: they are in a customer's history, in emails we have
            sent, and in the agent's own answers. A page that changes its
            address to change its neighbourhood breaks every link to it, and
            nothing about wrapping them in a rail requires that.
          */}
          <Route element={<SettingsLayout />}>
            <Route
              path="/settings/*"
              element={
                <RequireRole allow={['Owner', 'Admin']}>
                  <Lazy Component={Settings} />
                </RequireRole>
              }
            />
            <Route path="/users/*" element={<Lazy Component={Users} />} />
            {/* Owner, Admin and Finance only: a diff of an employee update
                shows a salary. */}
            <Route
              path="/activity"
              element={
                <RequireRole allow={['Owner', 'Admin', 'Finance']}>
                  <Lazy Component={ActivityPage} />
                </RequireRole>
              }
            />
            {/* NOT under RequireRole: everyone can read the facts shaping their
                own answers, and only writing a COMPANY memory is gated. See
                routes/erp/memory.ts. */}
            <Route path="/memory" element={<Lazy Component={MemoryPage} />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}
